import styled from '@emotion/styled';
import { Box, FormField } from '@kc-console/common-ui';

import { Separator } from '../DateTimeRangeSeparator';
import { useDateRangeHandlers } from '../hooks/useDateRangeHandlers';
import { useDateRangeValidation } from '../hooks/useDateRangeValidation';
import InlineDateTimePicker from './InlineDateTimePicker';

import type { BaseDateTimeRangePickerProps } from '../types';

/**
 * Inline 형태의 DateTimeRange Picker wrapper 컴포넌트의 Props입니다.
 *
 * @example
 * ```tsx
 * <InlineDateTimeRangePicker
 *   startDate={startDate}
 *   endDate={endDate}
 *   onChange={([start, end]) => {
 *     setStartDate(start);
 *     setEndDate(end);
 *   }}
 *   startLabel={<Label label="시작 날짜" htmlFor="start" />}
 *   endLabel={<Label label="종료 날짜" htmlFor="end" />}
 * />
 * ```
 */
export interface InlineDateTimeRangePickerProps
  extends Omit<BaseDateTimeRangePickerProps, 'inline'> {
  // BaseDateTimeRangePickerProps의 모든 속성을 상속하되 inline 제외
}

/**
 * 두 개의 InlineDateTimePicker를 나란히 배치하여 날짜 범위와 시간을 함께 선택할 수 있는 컴포넌트입니다.
 * Popper 없이 input 하단에 캘린더가 항상 표시됩니다.
 * FormField와 함께 사용되며, label은 각각 설정할 수 있고 나머지 props는 공통으로 적용됩니다.
 *
 * @remarks
 * - startDate는 항상 endDate보다 앞서야 합니다. (단, `allowEqualDates=true`이면 동일 일시는 허용됩니다.)
 * - 잘못된 날짜 범위가 전달되면 자동으로 보정됩니다.
 * - 비교 시 시분초를 포함하여 정확하게 검증합니다.
 */
const InlineDateTimeRangePicker = (props: InlineDateTimeRangePickerProps) => {
  const {
    startDate: startDateProp,
    endDate: endDateProp,
    onChange,
    startLabel,
    endLabel,
    startId,
    endId,
    formFieldProps,
    inputProps,
    allowEqualDates,
    name,
    ...dateTimePickerProps
  } = props;

  // 날짜 범위 검증
  const { validatedStartDate, validatedEndDate } = useDateRangeValidation(
    startDateProp,
    endDateProp,
    allowEqualDates,
  );

  // 날짜 변경 핸들러
  const { handleStartChange, handleEndChange } = useDateRangeHandlers(
    validatedStartDate,
    validatedEndDate,
    onChange,
    dateTimePickerProps.minDate,
    dateTimePickerProps.maxDate,
    allowEqualDates,
  );

  // inputProps 기본값 설정
  const enhancedInputProps = {
    size: 'md' as const,
    disabledOverflow: true,
    ...inputProps,
  };

  const inputSize = enhancedInputProps.size || 'md';
  const hasLabel = !!(startLabel || endLabel);
  const startName = name ? `${name}.start` : undefined;
  const endName = name ? `${name}.end` : undefined;

  return (
    <Container>
      <StyledFormField {...formFieldProps} label={startLabel}>
        <InlineDateTimePicker
          {...dateTimePickerProps}
          name={startName}
          selected={validatedStartDate}
          startDate={validatedStartDate}
          endDate={validatedEndDate}
          selectsStart
          inputProps={{
            ...enhancedInputProps,
            id: startId,
          }}
          onChange={handleStartChange}
        />
      </StyledFormField>
      <Separator size={inputSize} hasLabel={hasLabel}>
        ~
      </Separator>
      <StyledFormField {...formFieldProps} label={endLabel}>
        <InlineDateTimePicker
          {...dateTimePickerProps}
          name={endName}
          selected={validatedEndDate}
          startDate={validatedStartDate}
          endDate={validatedEndDate}
          selectsEnd
          inputProps={{
            ...enhancedInputProps,
            id: endId,
          }}
          onChange={handleEndChange}
        />
      </StyledFormField>
    </Container>
  );
};

InlineDateTimeRangePicker.displayName = 'InlineDateTimeRangePicker';

export default InlineDateTimeRangePicker;

const Container = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  &:has(.react-datepicker__week:nth-of-type(6)) .react-datepicker__month {
    min-height: 288px;
  }
  &:not(:has(.react-datepicker__week:nth-of-type(6))):has(.react-datepicker__week:nth-of-type(5))
    .react-datepicker__month {
    min-height: 240px;
  }
`;

const StyledFormField = styled(FormField)`
  width: 352px;
`;
